import React from "react";
import { Avatar, AvatarFallback } from "@/shared/components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/shared/components/ui/sidebar";

export interface ProfileMenuItem {
	label: string;
	icon: React.ElementType;
	isActive?: boolean;
	onClick: () => void;
	badge?: boolean;
	separator?: boolean;
}

export interface ProfileMenuProps {
	userDisplayName: string;
	userInitials: string;
	userEmail?: string;
	subtitle?: string;
	menuItems: ProfileMenuItem[];
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
	userDisplayName,
	userInitials,
	userEmail,
	subtitle,
	menuItems,
}) => {
	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className="py-6 px-4 cursor-pointer group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:justify-center"
						>
							<Avatar className="h-7 w-7 shrink-0">
								<AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs font-medium">
									{userInitials}
								</AvatarFallback>
							</Avatar>
							<div className="grid flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
								<span className="truncate font-medium text-sm text-sidebar-foreground">
									{userDisplayName}
								</span>
								{subtitle && (
									<span className="truncate text-xs text-sidebar-foreground/50">
										{subtitle}
									</span>
								)}
							</div>
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						side="top"
						align="start"
						sideOffset={8}
						className="w-[220px] rounded-lg p-0"
					>
						{/* Email */}
						<div className="px-3 py-2.5">
							<span className="text-[13px] text-muted-foreground">{userEmail}</span>
						</div>
						<DropdownMenuSeparator className="my-0" />

						{/* Items */}
						<div className="p-1">
							{menuItems.map((item, i) => (
								<React.Fragment key={item.label}>
									{item.separator && i > 0 && (
										<DropdownMenuSeparator className="my-1" />
									)}
									<DropdownMenuItem
										onClick={item.onClick}
										className="gap-2.5 px-2.5 py-2 rounded-md cursor-pointer text-[13px] focus:bg-accent/50"
									>
										<item.icon className="size-4 text-muted-foreground/70" />
										<span className="flex-1">{item.label}</span>
										{item.badge && (
											<span className="size-1.5 rounded-full bg-primary" />
										)}
									</DropdownMenuItem>
								</React.Fragment>
							))}
						</div>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	);
};
